// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

// Material Dashboard 2 React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import DataTable from "examples/Tables/DataTable";
import add from "assets/images/add.png";
import { useNavigate } from "react-router-dom";
import ChatScreen from "components/ChatScreen";
import DiscountForm from "components/Discount";
import { useGetAllReviewsMutation } from "services/rating";
import { useEffect, useState } from "react";
import MDInput from "components/MDInput";
import { Box, Dialog, DialogContent, FormControl, Rating } from "@mui/material";
import MDButton from "components/MDButton";
import MDAvatar from "components/MDAvatar";
import MDBadge from "components/MDBadge";
import { useRatingResponseMutation } from "services/rating";
import ShowToast from "components/MDToast";
import moment from "moment";
import Pagination from "components/Pagination";
import Loader from "helpers/constants/Loader";
import useTranslation from "hooks/Translation";

const ManageReview = () => {
  const [getAllReviews] = useGetAllReviewsMutation();
  const [vendorResponse] = useRatingResponseMutation();
  const navigate = useNavigate();
  const t = useTranslation();

  const [ratingData, setRatingData] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [error, setError] = useState(false);
  const [response, setResponse] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [totalRecords, setTotalRecords] = useState(10);
  const [loading, setLoading] = useState(false);

  const getData = async (page) => {
    setLoading(true);
    try {
      let body = {
        start: page,
        length: limit,
        search: "",
      };

      const res = await getAllReviews({ body: body }).unwrap();
      if (res?.status === 200) {
        setLoading(false);
        setRatingData(res?.data?.response_object || []);
        setTotalRecords(res?.data?.total_records);
      } else {
        setLoading(false);
      }
    } catch (error) {
      setLoading(false);
      console.error(error);
    }
  };

  const totalPages = Math.ceil(totalRecords / limit);

  const handleRevert = async (id) => {
    // Create the body object based on the status

    if (!response) {
      return;
    }

    const body = {
      vendor_response: response,
    };

    try {
      const response = await vendorResponse({
        body,
        id: id,
      }).unwrap();
      if (response?.status === 200) {
        ShowToast("Response Sent Successfully", "success");
        setOpen(false);
        getData();
      } else {
        ShowToast("Please try again", "error");
      }
    } catch {
      ShowToast("Something went wrong", "error");
    }
  };

  const [open, setOpen] = useState(false);

  const handleClose = () => {
    setOpen(false);
  };

  const Author = ({ image, name, email }) => (
    <MDBox display="flex" alignItems="center" lineHeight={1}>
      <MDAvatar src={image} name={name} size="sm" />
      <MDBox ml={2} lineHeight={1}>
        <MDTypography display="block" variant="button" fontWeight="medium">
          {name}
        </MDTypography>
        <MDTypography variant="caption">{email}</MDTypography>
      </MDBox>
    </MDBox>
  );

  const Job = ({ title, description }) => (
    <MDBox lineHeight={1} textAlign="left">
      <MDTypography display="block" variant="caption" fontWeight="medium">
        {title}
      </MDTypography>
      <MDTypography variant="caption">{description}</MDTypography>
    </MDBox>
  );

  const Name = ({ title, description }) => (
    <MDBox lineHeight={1} textAlign="left" sx={{ minWidth: "110px" }}>
      <MDTypography display="block" variant="caption" fontWeight="medium">
        {title}
      </MDTypography>
      <MDTypography variant="caption">{description}</MDTypography>
    </MDBox>
  );

  const columns = [
    { Header: t.main.products, accessor: "author", align: "left" },
    { Header: t.main.customer, accessor: "customer", align: "left" },
    { Header: t.main.email, accessor: "email", align: "left" },
    { Header: t.main.rating, accessor: "rating", align: "center" },
    { Header: t.main.review, accessor: "status", align: "left" },
    { Header: t.main.date, accessor: "date", align: "left" },

    { Header: t.main.actions, accessor: "action", align: "center" },
  ];

  const rows = ratingData?.map((item) => ({
    author: (
      <Author
        image={item?.product_review?.product_image[0]?.media_url}
        name={item?.product_review?.product_name_en || "-"}
      />
    ),
    customer: <Job title={item?.user?.full_name || "-"} />,
    email: <Job title={item?.user?.email || "-"} />,
    rating: (
      <Rating
        name="simple-controlled"
        value={item?.ratings}
        readOnly
        // onChange={(event, newValue) => {
        //   setValue(newValue);
        // }}
        size="small"
      />
    ),
    status: <Job title={item?.description || "-"} />,

    date: <Job title={moment(item?.created_at).format("MM/DD/YYYY")} />,
    action: (
      <MDBox>
        {item?.is_send_response ? (
          <MDBox sx={{ cursor: "pointer" }}>
            <MDBadge
              badgeContent="Responded"
              color="success"
              variant="gradient"
              size="md"
            />
          </MDBox>
        ) : (
          <MDBox
            sx={{ cursor: "pointer" }}
            onClick={() => {
              setSelectedId(item?.id);
              setOpen(true);
            }}
          >
            <MDBadge
              badgeContent={t.main.respond}
              color="success"
              variant="gradient"
              size="md"
            />
          </MDBox>
        )}
      </MDBox>
    ),
  }));

  useEffect(() => {
    getData(page);
  }, [page]);

  return (
    <DashboardLayout>
      <Loader loading={loading} />
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                // variant="gradient"
                bgColor="theme-1"
                borderRadius="lg"
                coloredShadow="info"
                sx={{ display: "flex", justifyContent: "space-between" }}
              >
                <MDTypography variant="h6" color="white">
                  {t.main.ratings}
                </MDTypography>
              </MDBox>
              <MDBox pt={3} pl={2} pr={2}>
                <DataTable
                  table={{ columns, rows }}
                  isSorted={false}
                  entriesPerPage={false}
                  showTotalEntries={false}
                  noEndBorder
                />
              </MDBox>

              {ratingData.length > 0 && (
                <MDBox p={2}>
                  <Pagination
                    setPage={setPage}
                    module={ratingData}
                    page={page}
                    onPageChange={setPage}
                    totalPages={totalPages}
                    limit={limit}
                    setLimit={setLimit}
                  />
                </MDBox>
              )}
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      <Dialog open={open} onClose={handleClose} className="revert_dialog">
        <DialogContent>
          <MDTypography variant="h5" fontWeight="medium" mb={1}>
            Respond to Review
          </MDTypography>
          <form>
            <FormControl fullWidth>
              <MDBox mb={2}>
                <MDInput
                  type="text"
                  placeholder="Description"
                  variant="outlined"
                  fullWidth
                  value={response}
                  onChange={(val) => setResponse(val?.target?.value)}
                  error={error && !response}
                />
              </MDBox>
            </FormControl>
            <Box className="form_btn">
              <MDButton
                onClick={handleClose}
                variant="gradient"
                color="success"
                fullWidth
              >
                Cancel
              </MDButton>
              <MDButton
                onClick={() => {
                  setError(true);
                  handleRevert(selectedId);
                }}
                variant="gradient"
                color="success"
                fullWidth
              >
                Send
              </MDButton>
            </Box>
          </form>
        </DialogContent>
      </Dialog>
      <Footer />
    </DashboardLayout>
  );
};

export default ManageReview;
